import { useEffect, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import { adminApi } from "../../api/admin.api.js";
import { useResource } from "../../hooks/useResource.js";
import { FormShell } from "../../components/forms/FormShell.jsx";
import { FormSection } from "../../components/forms/FormSection.jsx";
import { SlugField } from "../../components/forms/SlugField.jsx";
import { Input } from "../../components/ui/Input.jsx";
import { Textarea } from "../../components/ui/Textarea.jsx";
import { Select } from "../../components/ui/Select.jsx";
import { Switch } from "../../components/ui/Switch.jsx";
import { Button } from "../../components/ui/Button.jsx";
import { PageLoader } from "../../components/ui/Spinner.jsx";
import { ImageUpload } from "../../components/upload/ImageUpload.jsx";
import { GalleryUpload } from "../../components/upload/GalleryUpload.jsx";
import { PdfUpload } from "../../components/upload/PdfUpload.jsx";
import { RichTextEditor } from "../../components/forms/RichTextEditor.jsx";

const DEFAULTS = {
  name: "",
  slug: "",
  storey: "single",
  bedrooms: 4,
  bathrooms: 2,
  livingAreas: 1,
  garage: 2,
  areaSqm: "",
  areaSq: "",
  length: "",
  width: "",
  blockWidth: "",
  blockDepth: "",
  lotFits: "",
  heroImage: "",
  images: [],
  floorPlanImages: [],
  brochurePdf: "",
  floorPlanPdf: "",
  description: "",
  address: "",
  facades: [],
  matterport: null,
  seo: { title: "", description: "", ogImage: "" },
  isFeatured: false,
  status: "draft",
  sortOrder: 0,
};

/**
 * Strip empty/derived fields and coerce numerics before submit. The backend
 * Zod schemas accept strings and coerce, but sending clean payloads keeps the
 * audit log meta less noisy.
 */
function buildPayload(values) {
  const numeric = [
    "bedrooms",
    "bathrooms",
    "livingAreas",
    "garage",
    "areaSqm",
    "areaSq",
    "length",
    "width",
    "blockWidth",
    "blockDepth",
    "sortOrder",
  ];
  const out = { ...values };
  for (const k of numeric) {
    if (out[k] === "" || out[k] === null) {
      delete out[k];
    } else {
      out[k] = Number(out[k]);
    }
  }
  if (!out.slug) delete out.slug;
  if (!out.matterport) delete out.matterport;
  return out;
}

export default function DesignFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isNew = id === "new" || !id;

  // Pre-fill from Smart Upload — when the user comes via
  // navigate('/designs/new', { state: { prefill: {...} } }), we hydrate
  // the form on first mount with everything the backend extracted.
  const prefill = useMemo(() => location.state?.prefill ?? null, [location.state]);

  const { detail, create, update } = useResource("designs", { toastLabel: "design" });
  const { data: existing, isLoading } = detail(isNew ? null : id);

  const matterportQuery = useQuery({
    queryKey: ["matterport", "list-for-form"],
    queryFn: () => adminApi.list("matterport", { limit: 100 }),
    staleTime: 5 * 60_000,
  });

  const facadesQuery = useQuery({
    queryKey: ["facades", "list-for-form"],
    queryFn: () => adminApi.list("facades", { limit: 100 }),
    staleTime: 5 * 60_000,
  });

  const form = useForm({
    defaultValues: prefill ? { ...DEFAULTS, ...prefill } : DEFAULTS,
  });
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  // Hydrate the form once the existing record loads.
  useEffect(() => {
    if (existing) {
      reset({
        ...DEFAULTS,
        ...existing,
        // Mongoose returns populated facades + matterport — flatten.
        facades: (existing.facades ?? []).map((f) => (typeof f === "object" ? f._id : f)),
        matterport:
          typeof existing.matterport === "object" ? existing.matterport?._id : existing.matterport,
        seo: existing.seo ?? DEFAULTS.seo,
      });
    }
  }, [existing, reset]);

  useEffect(() => {
    document.title = isNew ? "New Design · Admin" : "Edit Design · Admin";
  }, [isNew]);

  const onSubmit = handleSubmit(async (values) => {
    const payload = buildPayload(values);
    if (isNew) {
      await new Promise((resolve, reject) => {
        create.mutate(payload, {
          onSuccess: (created) => {
            navigate(`/designs/${created._id}`);
            resolve();
          },
          onError: reject,
        });
      });
    } else {
      await new Promise((resolve, reject) => {
        update.mutate({ id, data: payload }, { onSuccess: resolve, onError: reject });
      });
    }
  });

  if (!isNew && isLoading) return <PageLoader />;

  const watchedName = watch("name");
  const watchedSlug = watch("slug");

  return (
    <FormShell
      title={isNew ? "New Home Design" : `Edit "${existing?.name ?? ""}"`}
      backTo="/designs"
      backLabel="Back to designs"
      onSubmit={onSubmit}
      primaryAction={
        <Button type="submit" loading={isSubmitting} form-action="">
          {isNew ? "Create design" : "Save changes"}
        </Button>
      }
      secondaryAction={
        <Button type="button" variant="secondary" onClick={() => navigate("/designs")}>
          Cancel
        </Button>
      }
      sidebar={
        <>
          <FormSection title="Status">
            <Select label="Status" {...register("status")}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </Select>
            <Controller
              control={control}
              name="isFeatured"
              render={({ field }) => (
                <Switch
                  label="Featured on homepage"
                  hint="Featured designs appear in the homepage strip."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
            <Input
              type="number"
              label="Sort order"
              hint="Lower = appears first in the catalogue."
              {...register("sortOrder")}
            />
          </FormSection>

          <FormSection title="SEO">
            <Input
              label="SEO title"
              hint="Defaults to design name if blank. Max 160 chars."
              {...register("seo.title")}
            />
            <Textarea
              label="SEO description"
              hint="Max 320 chars. Shows in Google snippets."
              rows={3}
              {...register("seo.description")}
            />
            <Input
              label="OG image path"
              hint="/uploads/... or leave blank"
              {...register("seo.ogImage")}
            />
          </FormSection>
        </>
      }
    >
      <FormSection title="Basics" description="Name and slug.">
        <Input
          label="Name"
          required
          error={errors.name?.message}
          {...register("name", { required: "Name is required" })}
        />
        <SlugField value={watchedSlug} onChange={(v) => setValue("slug", v)} source={watchedName} />
      </FormSection>

      <FormSection title="Specifications" description="Used by the public filter sidebar.">
        <div className="grid grid-cols-2 gap-4">
          <Select label="Storey" required {...register("storey")}>
            <option value="single">Single storey</option>
            <option value="double">Double storey</option>
          </Select>
          <Input
            type="number"
            min="0"
            max="20"
            label="Bedrooms"
            required
            {...register("bedrooms")}
          />
          <Input
            type="number"
            min="0"
            max="20"
            label="Bathrooms"
            required
            {...register("bathrooms")}
          />
          <Input type="number" min="0" label="Living areas" {...register("livingAreas")} />
          <Input type="number" min="0" label="Garage" {...register("garage")} />
          <Input type="number" step="0.1" min="0" label="Area (sqm)" {...register("areaSqm")} />
          <Input type="number" step="0.1" min="0" label="Area (sq)" {...register("areaSq")} />
          <Input type="number" step="0.1" min="0" label="Length (m)" {...register("length")} />
          <Input type="number" step="0.1" min="0" label="Width (m)" {...register("width")} />
          <Input
            type="number"
            step="0.1"
            min="0"
            label="Block width (m)"
            {...register("blockWidth")}
          />
          <Input
            type="number"
            step="0.1"
            min="0"
            label="Block depth (m)"
            {...register("blockDepth")}
          />
          <Input
            label="Lot fits caption"
            placeholder="Fits lot 12.5m x 26m"
            {...register("lotFits")}
            className="col-span-2"
          />
        </div>
      </FormSection>

      <FormSection
        title="Description"
        description="Rendered as rich text on the public detail page."
      >
        <Controller
          control={control}
          name="description"
          render={({ field }) => (
            <RichTextEditor value={field.value} onChange={field.onChange} subdir="designs" />
          )}
        />
      </FormSection>

      <FormSection
        title="Hero image"
        description="The single shot that headlines the design — used in cards and the detail-page hero."
      >
        <Controller
          control={control}
          name="heroImage"
          render={({ field }) => (
            <ImageUpload
              value={field.value}
              onChange={field.onChange}
              subdir="designs"
              aspect="4/3"
              hint="Recommended: 1600 px wide, landscape. Falls back to the first gallery image if blank."
            />
          )}
        />
      </FormSection>

      <FormSection title="Gallery" description="Drag to reorder. Photoshoot images of the home.">
        <Controller
          control={control}
          name="images"
          render={({ field }) => (
            <GalleryUpload
              value={field.value}
              onChange={field.onChange}
              subdir="designs"
              label="Photos"
              max={20}
            />
          )}
        />
      </FormSection>

      <FormSection
        title="Floor plan"
        description="The branded floor-plan tile + the original PDF for download."
      >
        <Controller
          control={control}
          name="floorPlanImages"
          render={({ field }) => (
            <GalleryUpload
              value={field.value}
              onChange={field.onChange}
              subdir="designs"
              label="Floor plan images"
              hint="Drop branded floor plans here. Smart Upload pre-populates the first one automatically."
              max={10}
            />
          )}
        />
        <Controller
          control={control}
          name="floorPlanPdf"
          render={({ field }) => (
            <PdfUpload
              value={field.value}
              onChange={field.onChange}
              subdir="brochures"
              label="Floor plan PDF"
              hint="The architectural drawings PDF. Public 'Download plans' button links to it."
            />
          )}
        />
      </FormSection>

      <FormSection
        title="Site address"
        description="Where the working drawings were prepared for. Renders as a Google Maps embed on the public detail page."
      >
        <Input
          label="Property address"
          placeholder="12 Sullivan Street, Lucas VIC 3350"
          hint="Optional. Leave blank to hide the map section on the public page."
          {...register("address")}
        />
      </FormSection>

      <FormSection title="Brochure & 3D Tour">
        <Controller
          control={control}
          name="brochurePdf"
          render={({ field }) => (
            <PdfUpload
              value={field.value}
              onChange={field.onChange}
              subdir="brochures"
              label="Marketing brochure PDF (optional)"
              hint="Separate from the floor plan PDF — used for sales kits."
            />
          )}
        />
        <Select label="Linked Matterport tour (optional)" {...register("matterport")}>
          <option value="">No 3D tour linked</option>
          {matterportQuery.data?.items?.map((m) => (
            <option key={m._id} value={m._id}>
              {m.title}
            </option>
          ))}
        </Select>
      </FormSection>

      <FormSection title="Linked facades (optional)">
        <Controller
          control={control}
          name="facades"
          render={({ field }) => (
            <FacadeMultiSelect
              value={field.value}
              onChange={field.onChange}
              options={facadesQuery.data?.items ?? []}
            />
          )}
        />
      </FormSection>
    </FormShell>
  );
}

function FacadeMultiSelect({ value = [], onChange, options }) {
  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };
  if (options.length === 0) {
    return (
      <p className="text-sm text-text-muted">
        No facades exist yet. Create some on the Facades page first.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      {options.map((f) => (
        <label
          key={f._id}
          className="flex items-center gap-3 rounded-md border border-border-subtle bg-bg-elevated-2 px-3 py-2 text-sm hover:border-border"
        >
          <input
            type="checkbox"
            checked={value.includes(f._id)}
            onChange={() => toggle(f._id)}
            className="h-4 w-4 rounded border-border bg-bg-base text-accent focus:ring-accent"
          />
          <span className="text-text-primary">{f.title}</span>
        </label>
      ))}
    </div>
  );
}
